<script lang="ts" context="module">
    import Modal, { createModal } from '$components/Modal.svelte';

    export { createModal };
</script>

<script lang="ts">
    import {
        ReactionAuthorFragmentDoc,
        ReactionsQueryDocument,
        type ReactionsQueryQueryVariables,
        type ReactionTargetInput,
    } from '$gql/graphql.js';
    import ReactionsListModalCounters from './ReactionsListModalCounters.svelte';
    import { query } from '$lib/apollo/SvelteApollo.js';
    import { useFragment } from '$gql/fragment-masking.js';
    import { NetworkStatus } from '@apollo/client/core/core.cjs';
    import ReactionsListModalItem from './ReactionsListModalItem.svelte';
    import ReactionsListModalItemLoader from './ReactionsListModalItemLoader.svelte';
    import { fetchMore } from '$lib/utils/interractions.js';
    import uniqBy from 'lodash/uniqBy';
    import { melt } from '@melt-ui/svelte';
    import { readable } from 'svelte/store';
    import { getContainer } from '$components/Context/ContainerContext';

    export let target: ReactionTargetInput;
    export let counters: { count: number; type: string }[];
    export let modal: ReturnType<typeof createModal>;

    const {
        elements: { title, close },
        states: { open },
    } = modal;

    const MAX_PER_PAGE = 10;

    let type: string | null = null;

    let variables: ReactionsQueryQueryVariables = {
        target,
        page: 1,
        maxPerPage: MAX_PER_PAGE,
        type,
    };

    $: ({
        result,
        setVariables,
        fetchMore: loadMore,
    } = !$open
        ? { result: readable(null), setVariables: null, fetchMore: null }
        : query(ReactionsQueryDocument, {
              variables: variables,
              notifyOnNetworkStatusChange: true,
          }));

    $: variables = { ...variables, target, type, page: 1 };
    $: setVariables?.(variables);

    const currentUserId = getContainer().currentUserStore().currentUserId();

    $: pagerInfo = $result?.data?.reactions.pagerInfo;
    $: currentPage = pagerInfo?.page ?? 1;
    $: nbPages = pagerInfo?.nbPages ?? 0;
    $: loading = $result?.loading ?? true;
    $: networkStatus = $result?.networkStatus;

    function loadMoreReactions() {
        if (loading || currentPage >= nbPages) {
            return;
        }

        loadMore?.({
            variables: { page: currentPage + 1, maxPerPage: MAX_PER_PAGE },
            updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                return {
                    ...fetchMoreResult,
                    reactions: {
                        ...fetchMoreResult.reactions,
                        results: uniqBy(
                            [
                                ...previousQueryResult.reactions.results,
                                ...fetchMoreResult.reactions.results,
                            ],
                            'id',
                        ),
                    },
                };
            },
        });
    }
</script>

<Modal
    contentClass="ls:max-w-[900px] ls:min-w-[600px] rounded-t-2xl"
    dialogClass="p-0 items-start"
    {modal}
>
    <div class="p-5 border-0 text-left">
        <button use:melt={$close} class="close">
            <span aria-hidden="true">&times;</span>
        </button>
        <h4 use:melt={$title} class="modal-title m-0 text-18 ls:text-22">Réactions</h4>
    </div>

    <div class="text-left p-5">
        <ReactionsListModalCounters
            {counters}
            {type}
            on:select={(e) => (type = e.detail)}
        />
        <div
            class={'max-h-[60vh] overflow-auto overscroll-contain'}
            use:fetchMore={{ scrollFrom: 'node' }}
            on:fetchmore={loadMoreReactions}
        >
            {#if !loading || networkStatus === NetworkStatus.fetchMore}
                <div class={'flex flex-col gap-2'}>
                    {#each $result?.data?.reactions.results || [] as reaction (reaction.id)}
                        <ReactionsListModalItem
                            type={reaction.type}
                            author={useFragment(
                                ReactionAuthorFragmentDoc,
                                reaction.author,
                            )}
                            currentUserId={$currentUserId}
                        />
                    {/each}
                </div>
            {/if}

            {#if loading}
                <ReactionsListModalItemLoader />
            {/if}

            {#if $result?.error != null}
                <div class={'alert alert-danger my-3'}>
                    {$result.error.message}
                </div>
            {/if}
        </div>
    </div>
</Modal>
